import React, { useEffect, useState } from 'react';
import { Check, FileDownload, FileUpload, SwapVert, Warning } from '@mui/icons-material';
import { Button, Card, CardContent, Divider, LinearProgress, Slider, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { BulkService } from '/src/api/services/bulk-service';
import { Box, ListItem } from '/src/components';
import { useBulkStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Nullable, UploadStatus } from '/src/lib/types';

/**
 * Bulk Upload Page
 */
export const BulkPage: React.FC = observer(() => {
  /** Context **/
  const bulkStore = useBulkStore();

  /** Ref **/

  /** State **/
  const [success, setSuccess] = useState(false);
  const [wrongFile, setWrongFile] = useState<string>();
  const [importProgressBar, setImportProgressBar] = useState(1);
  const [queue, setQueue] = useState<FileList>({} as FileList);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(UploadStatus.Ready);

  /** Computed **/

  /**
   * Data must be imported in this order.
   */
  const steps: Record<number, { title: string; fileName?: string }> = {
    1: { title: 'Step 1: Import Companies', fileName: 'company.csv' },
    2: { title: 'Step 2: Import Deals', fileName: 'loan.csv' },
    3: { title: 'Step 3: Import Participations', fileName: 'participation.csv' },
    4: { title: 'Step 4: Import Assets', fileName: 'asset.csv' },
    5: { title: 'Step 5: Import Advances', fileName: 'advance.csv' },
    6: { title: 'Step 6: Import Payments', fileName: 'payment.csv' },
    7: { title: 'Database Import Complete!' },
  };

  const fileForm = document.getElementById('fileForm') as Nullable<HTMLFormElement>;
  const fileInput = document.getElementById('fileInput') as Nullable<HTMLInputElement>;

  /** Methods **/

  const openFileDialog = () => {
    if (uploadStatus !== UploadStatus.Uploading) fileInput?.click();
  };

  /**
   * Validates and queues files for upload.
   *
   * @param files List of files to upload. Can be null.
   */
  const handleFileSelect = (files: Nullable<FileList>) => {
    if (files) {
      if (steps[importProgressBar] && files?.[0].name !== steps[importProgressBar].fileName) {
        setWrongFile(steps[importProgressBar].fileName);
        setUploadStatus(UploadStatus.Ready);
      } else {
        setQueue(files);
        setUploadStatus(UploadStatus.Queued);
      }
    }
  };

  /** Effects **/

  useEffect(() => {
    if (queue.length && uploadStatus === UploadStatus.Queued) {
      const upload = async () => {
        if (!fileForm) return;

        setUploadStatus(UploadStatus.Uploading);

        const request = new FormData(fileForm);
        await BulkService.import(request);

        setSuccess(true);
        setImportProgressBar(importProgressBar + 1);
        setUploadStatus(UploadStatus.Ready);
      };

      upload();
    }
  }, [queue, uploadStatus, fileForm, importProgressBar]);

  /** Render **/
  return (
    <DesktopLayout>
      <Box className="md-content">
        <Box layout-align="center top">
          <Box width="50%">
            <Card>
              <Box className="toolbar-div pad-top-bottom-xs" layout-align="center center">
                <Box component="span" display="flex" />

                <Box display="flex" layout-align="center center">
                  <SwapVert className="md-icons material-icons" />
                  <Box component="span" className="md-title pad-left-xs">
                    Data Import/Export
                  </Box>
                </Box>

                <Box component="span" display="flex" />
              </Box>

              <CardContent>
                <ListItem layout-align="space-between center" className="margin-bottom-sm">
                  <Box component="span" display="flex" flexDirection="column">
                    <Box component="span" className="textZoomIn">
                      Import Data
                    </Box>

                    <Box component="span" className="text-size-md">
                      Files must be in CSV format.
                    </Box>
                  </Box>

                  <Button
                    className="md-fab md-mini md-primary"
                    onClick={() => openFileDialog()}
                    disabled={uploadStatus === UploadStatus.Uploading}
                  >
                    <Tooltip title="Import Data">
                      <FileUpload className="md-icons material-icons" />
                    </Tooltip>
                  </Button>
                </ListItem>

                <ListItem>
                  <Slider
                    className="md-primary"
                    onChange={(_, step) => {
                      if (typeof step === 'number') setImportProgressBar(step);
                    }}
                    value={importProgressBar}
                    disabled
                    step={null}
                    marks={new Array(7).map((_, i) => ({ value: i + 1 }))}
                    min={1}
                    max={7}
                    sx={{ display: 'flex' }}
                  />
                </ListItem>

                <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box component="span" className="md-title text-sm-mobile">
                    {steps[importProgressBar].title}
                  </Box>
                </ListItem>

                <form name="fileForm" id="fileForm">
                  <input
                    type="file"
                    id="fileInput"
                    name="fileInput"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                  />
                </form>
              </CardContent>

              {(bulkStore.returnMessage?.saved.length ||
                bulkStore.returnMessage?.errors.length ||
                bulkStore.error ||
                wrongFile) && <Divider />}

              {(bulkStore.returnMessage?.saved.length ||
                bulkStore.returnMessage?.errors.length ||
                bulkStore.error ||
                uploadStatus === UploadStatus.Uploading ||
                wrongFile) && (
                <CardContent
                  sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
                >
                  {uploadStatus === UploadStatus.Uploading && (
                    <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <LinearProgress className="md-primary" md-mode="determinate" />
                    </ListItem>
                  )}

                  {bulkStore.error && (
                    <ListItem className="text-sm">
                      <Warning className="md-icon material-icons pad-right-xs" />
                      <Box component="span" className="thin">
                        There was an error importing the data.
                      </Box>
                    </ListItem>
                  )}

                  {wrongFile && (
                    <ListItem className="text-sm">
                      <Warning className="md-icon material-icons pad-right-xs" />
                      <Box component="span" className="thin">
                        Please select a file named {wrongFile}.
                      </Box>
                    </ListItem>
                  )}

                  {bulkStore.returnMessage?.saved.length &&
                    uploadStatus !== UploadStatus.Uploading &&
                    !bulkStore.error &&
                    success && (
                      <ListItem
                        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                        className="text-sm"
                      >
                        <Check className="md-icon material-icons pad-right-xs" />
                        <Box component="span" className="thin">
                          <Box component="span" className="medium">
                            {bulkStore.returnMessage?.saved.length}
                          </Box>{' '}
                          Items imported successfully.
                        </Box>
                      </ListItem>
                    )}
                </CardContent>
              )}

              {uploadStatus === UploadStatus.Uploading && (
                <CardContent>
                  <Divider />
                  <LinearProgress className="md-primary" md-mode="determinate" />
                </CardContent>
              )}

              {bulkStore.exportIsActive && (
                <CardContent>
                  <Divider />

                  <ListItem sx={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Box component="span" className="textZoomIn">
                      Export Transactions
                    </Box>

                    <Box component="span" display="flex" />

                    <Button
                      className="md-fab md-mini md-primary"
                      onClick={() => openFileDialog()}
                      disabled={uploadStatus === UploadStatus.Uploading}
                    >
                      <Tooltip title="Export Transactions">
                        <FileDownload className="md-icons material-icons" />
                      </Tooltip>
                    </Button>
                  </ListItem>
                </CardContent>
              )}
            </Card>
          </Box>
        </Box>
      </Box>
    </DesktopLayout>
  );
});
